import { useMemo, useState } from "react";
import AttachmentPreview from "@/business/notify/AttachmentPreview";
import { Button } from "@/Button";
import { Dialog } from "@/Dialog";
import MessageModal from "@/MessageModal";
import DateButton from "@/common/DateButton";
import ToggleSwitch from "@/common/ToggleSwitch";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import useOpenModal from "^/common/useOpenModal";
import { formatDate } from "%/date";
import {
  canUseNativePhotoPicker,
  openNativePhotoPicker,
} from "%/nativePhotoPicker";
import {
  MAX_NOTICE_ATTACHMENTS,
  NOTICE_ATTACHMENT_LIMIT_MESSAGE,
  mergeNoticeAttachments,
} from "%/noticeAttachmentPolicy";
import {
  useBusinessNoticeSelectList,
  useBusinessNoticeAlbumList,
} from "~/services/business/notify";
import {
  useCreateStoreFeed,
  useUpdateStoreFeed,
  useMyStoreFeed,
  useDeleteStoreFeed,
  type StoreFeed,
} from "~/services/business/feed";

interface Img {
  ImgID: number;
  ImgURL: string;
  thumbnailUrl?: string | null;
  thumbnailStatus?: string | null;
}

const FEED_CONTENT_MAX = 200;

const toYmd = (d: Date) => {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const SendFeed = () => {
  const { data: selectList } = useBusinessNoticeSelectList();
  const albumListMutation = useBusinessNoticeAlbumList();
  const createMutation = useCreateStoreFeed();
  const updateMutation = useUpdateStoreFeed();
  const deleteMutation = useDeleteStoreFeed();
  const {
    data: feedPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyStoreFeed();

  const [content, setContent] = useState("");
  const [images, setImages] = useState<Img[]>([]);
  const [useEventDate, setUseEventDate] = useState(false);
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [editingSeq, setEditingSeq] = useState<number | null>(null);

  const calendarModal = useOpenModal();
  const albumModal = useOpenModal();
  const [albumData, setAlbumData] = useState<Img[]>([]);
  const [modalSelected, setModalSelected] = useState<Img[]>([]);
  const [showDone, setShowDone] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StoreFeed | null>(null);

  const myFeeds = useMemo<StoreFeed[]>(
    () => (feedPages?.pages ?? []).flatMap((p) => p.content ?? []),
    [feedPages],
  );

  // 전달 대상 확인용 — 재원 반려견 이름 목록(연동 수신 목록 기준).
  const recipientPetNames = useMemo<string[]>(
    () =>
      (selectList?.recipients ?? [])
        .map((r: any) => r?.petName)
        .filter((n: any): n is string => !!n),
    [selectList],
  );

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const canSubmit =
    (content.trim().length > 0 || images.length > 0) && !isSubmitting;

  const resetForm = () => {
    setContent("");
    setImages([]);
    setUseEventDate(false);
    setEventDate(null);
    setEditingSeq(null);
  };

  const handlePickPhotos = async () => {
    const remaining = MAX_NOTICE_ATTACHMENTS - images.length;
    if (remaining <= 0) {
      alert(NOTICE_ATTACHMENT_LIMIT_MESSAGE);
      return;
    }
    if (canUseNativePhotoPicker()) {
      try {
        const picked = await openNativePhotoPicker({ multiSelect: remaining });
        if (picked.length) {
          const merged = mergeNoticeAttachments(
            images,
            picked
              .filter((p) => !!p.fileSeq)
              .map((p) => ({ ImgID: p.fileSeq, ImgURL: p.fileUrl }) as Img),
          );
          if (merged.length > MAX_NOTICE_ATTACHMENTS) {
            alert(NOTICE_ATTACHMENT_LIMIT_MESSAGE);
            return;
          }
          setImages(merged);
        }
      } catch (err) {
        alert(
          err instanceof Error ? err.message : "사진첩을 여는 데 실패했습니다.",
        );
      }
      return;
    }
    // 웹 폴백 — 우리 매장 클라우드 앨범(전체 반려견) 모달
    const recipients = selectList?.recipients ?? [];
    if (recipients.length === 0) {
      alert(
        "불러올 매장 사진이 없습니다. 앱에서 스마트폰 사진을 첨부해주세요.",
      );
      return;
    }
    albumListMutation.mutate(
      {
        date: null,
        recipients: recipients.map((r: any) => ({
          petSeq: r.petSeq,
          userSeq: r.petSeq,
          petName: r.petName,
          selected: true,
        })),
      },
      {
        onSuccess: (response: any) => {
          const list = Array.isArray(response.data) ? response.data : [];
          setAlbumData(
            list.map((item: any) => ({
              ImgID: item.fileSeq ?? item.notiPhotoSeq ?? 0,
              ImgURL: item.fileUrl ?? "",
              thumbnailUrl: item.thumbnailUrl ?? null,
              thumbnailStatus: item.thumbnailStatus ?? null,
            })),
          );
          setModalSelected([]);
          albumModal.clickModal();
        },
        onError: () => setAlbumData([]),
      },
    );
  };

  const toggleAlbumImage = (img: Img) => {
    setModalSelected((prev) =>
      prev.find((i) => i.ImgID === img.ImgID)
        ? prev.filter((i) => i.ImgID !== img.ImgID)
        : [...prev, img],
    );
  };

  const confirmAlbum = () => {
    const merged = mergeNoticeAttachments(images, modalSelected);
    if (merged.length > MAX_NOTICE_ATTACHMENTS) {
      alert(NOTICE_ATTACHMENT_LIMIT_MESSAGE);
      return;
    }
    setImages(merged);
    albumModal.closeModal();
  };

  const removeImage = (img: Img) =>
    setImages((prev) => prev.filter((i) => i.ImgID !== img.ImgID));

  const startEdit = (feed: StoreFeed) => {
    setEditingSeq(feed.feedSeq);
    setContent(feed.content ?? "");
    setImages(
      (feed.photos ?? []).map((p) => ({
        ImgID: p.fileSeq,
        ImgURL: p.fileUrl ?? "",
        thumbnailUrl: p.thumbnailUrl,
        thumbnailStatus: p.thumbnailStatus,
      })),
    );
    if (feed.dtEvent) {
      setUseEventDate(true);
      setEventDate(new Date(feed.dtEvent));
    } else {
      setUseEventDate(false);
      setEventDate(null);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = () => {
    if (!canSubmit) return;
    const req = {
      content: content.trim() || null,
      dtEvent: useEventDate && eventDate ? toYmd(eventDate) : null,
      imageFileSeqs: images.map((i) => i.ImgID),
    };
    const onSuccess = () => {
      resetForm();
      setShowDone(true);
    };
    if (editingSeq != null) {
      updateMutation.mutate({ feedSeq: editingSeq, req }, { onSuccess });
    } else {
      createMutation.mutate(req, { onSuccess });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.feedSeq, {
      onSuccess: () => {
        if (editingSeq === deleteTarget.feedSeq) resetForm();
        setDeleteTarget(null);
      },
    });
  };

  return (
    <>
      <div className="flex flex-col flex-auto gap-1 px-5 bg-[#F5F5F5] animate-fadeIn">
        <p className="mb-3 mt-1 flex items-center gap-1.5 text-[11px] text-gray-500">
          <span aria-hidden>📷</span> 사진·영상 중심으로 일상·행사 소식을 남겨요
        </p>

        {/* 사진·영상 — 소식의 중심 (그리드 컴포저, 최대 100장) */}
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium">사진·영상</p>
            <span className="text-[11px] text-gray-400">
              {images.length}/{MAX_NOTICE_ATTACHMENTS}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {images.length < MAX_NOTICE_ATTACHMENTS && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handlePickPhotos();
                }}
                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-orange-300 bg-white text-orange-400 transition-colors hover:bg-orange-50"
              >
                <span className="text-2xl leading-none">＋</span>
                <span className="text-[11px] font-medium">사진·영상</span>
              </button>
            )}
            {images.map((data) => (
              <div
                key={data.ImgID}
                className="relative aspect-square overflow-hidden rounded-lg bg-gray-100"
              >
                <AttachmentPreview
                  fileSeq={data.ImgID}
                  originalUrl={data.ImgURL}
                  thumbnailUrl={data.thumbnailUrl}
                  thumbnailStatus={data.thumbnailStatus}
                />
                <button
                  type="button"
                  className="flex absolute top-1 right-1 z-10 justify-center items-center w-5 h-5 bg-black/45 rounded-full hover:bg-black/65"
                  onClick={() => removeImage(data)}
                  style={{ lineHeight: 1 }}
                  aria-label="삭제"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 4L12 12"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M12 4L4 12"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          {images.length === 0 && (
            <p className="mt-2 text-[11px] text-gray-400">
              최대 {MAX_NOTICE_ATTACHMENTS}장 · 스마트폰 사진·영상을 바로 올릴
              수 있어요
            </p>
          )}
        </div>

        {/* 한 줄 소식 — 사진 중심의 짧은 캡션 (알림장처럼 길게 쓰는 용도 아님) */}
        <div className="my-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium">
              한 줄 소식{" "}
              <span className="text-xs font-normal text-gray-400">(선택)</span>
            </p>
            <span className="text-[11px] text-gray-400">
              {content.length}/{FEED_CONTENT_MAX}
            </span>
          </div>
          <textarea
            value={content}
            onChange={(e) =>
              setContent(e.target.value.slice(0, FEED_CONTENT_MAX))
            }
            placeholder="예) 오늘 가을 소풍 다녀왔어요 🐾"
            rows={2}
            maxLength={FEED_CONTENT_MAX}
            className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
          />
        </div>

        {/* 행사일 (선택) */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium">
              행사일{" "}
              <span className="text-xs font-normal text-gray-400">(선택)</span>
            </p>
            <ToggleSwitch
              checked={useEventDate}
              onChange={(c: boolean) => {
                setUseEventDate(c);
                if (c && !eventDate) setEventDate(new Date());
              }}
            />
          </div>
          {useEventDate && (
            <DateButton
              value={eventDate ? formatDate(eventDate) : "날짜 선택"}
              onClick={() => calendarModal.clickModal()}
            />
          )}
        </div>

        {/* 발송 대상 안내 — 재원 반려견 이름으로 확인 */}
        <div className="mb-24 rounded-lg bg-[#FFF6EC] px-3 py-2.5 text-[#8A4E10]">
          <div className="flex items-center gap-2 text-[12px] font-medium">
            <span aria-hidden>👥</span>
            재원 중인 보호자
            {recipientPetNames.length > 0
              ? ` ${recipientPetNames.length}마리`
              : " 전체"}
            에게 전달됩니다.
          </div>
          {recipientPetNames.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {recipientPetNames.map((name, i) => (
                <span
                  key={`${name}-${i}`}
                  className="rounded-full border border-[#F0D9BC] bg-white/70 px-2 py-0.5 text-[11px] text-[#8A4E10]"
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 내가 올린 소식 */}
        {myFeeds.length > 0 && (
          <div className="mb-28">
            <div className="mb-2 text-sm font-medium text-gray-700">
              내가 올린 소식
            </div>
            <div className="flex flex-col gap-2">
              {myFeeds.map((feed) => (
                <div
                  key={feed.feedSeq}
                  className={`flex items-center gap-2.5 rounded-xl bg-white p-2.5 border ${
                    editingSeq === feed.feedSeq
                      ? "border-orange-400"
                      : "border-gray-100"
                  }`}
                >
                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                    {feed.photos?.[0] ? (
                      <AttachmentPreview
                        fileSeq={feed.photos[0].fileSeq}
                        originalUrl={feed.photos[0].fileUrl ?? ""}
                        thumbnailUrl={feed.photos[0].thumbnailUrl}
                        thumbnailStatus={feed.photos[0].thumbnailStatus}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-300">
                        <span aria-hidden>📝</span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-gray-800">
                      {feed.content || "(사진 소식)"}
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-400">
                      {feed.dtRegist?.slice(0, 10)}
                      {feed.photos?.length
                        ? ` · 사진 ${feed.photos.length}`
                        : ""}
                      {` · 조회 ${feed.viewCount ?? 0}`}
                      {feed.dtEvent ? ` · 행사 ${feed.dtEvent.slice(5)}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:border-orange-400 hover:text-orange-500"
                    onClick={() => startEdit(feed)}
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-200 px-2 py-1 text-[11px] text-red-500 hover:border-red-400"
                    onClick={() => setDeleteTarget(feed)}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
            {hasNextPage && (
              <button
                type="button"
                className="mt-3 w-full rounded-md border border-gray-200 py-2 text-xs text-gray-500"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "불러오는 중…" : "더 보기"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 하단 고정 액션 */}
      <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-[480px] bottom-0 md:bottom-6 md:rounded-b-3xl z-40 px-4 pt-3 pb-[calc(0.75rem+var(--nav-bottom,0px)+env(safe-area-inset-bottom,0px))] bg-white border-t border-gray-200 shadow-lg flex gap-2">
        {editingSeq != null && (
          <Button
            className="w-full bg-transparent"
            style={{ pointerEvents: "auto" }}
            onClick={resetForm}
          >
            <div className="py-3 mx-auto w-full font-semibold text-center text-gray-600 bg-white rounded-full border border-gray-300 text-[16px]">
              취소
            </div>
          </Button>
        )}
        <Button
          className="w-full bg-transparent"
          style={{ pointerEvents: "auto" }}
          onClick={submit}
          disabled={!canSubmit}
        >
          <div
            className={`py-3 mx-auto w-full font-semibold text-center text-white rounded-full text-[16px] transition-all duration-300 ${
              canSubmit ? "bg-orange-400 hover:bg-orange-500" : "bg-orange-200"
            }`}
          >
            {editingSeq != null ? "소식 수정" : "소식 올리기"}
          </div>
        </Button>
      </div>

      {/* 달력 모달 */}
      <MessageModal
        isOpen={calendarModal.isOpenModal}
        onRequestClose={calendarModal.closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          dayOnClick={(day: Date | null) => day && setEventDate(day)}
          selectedDay={eventDate}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2 px-4">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={calendarModal.closeModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={calendarModal.closeModal}
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 앨범 모달 (웹 폴백) */}
      <MessageModal
        isOpen={albumModal.isOpenModal}
        onRequestClose={albumModal.closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col gap-2 overflow-y-auto h-[500px] p-2">
          <div className="grid grid-cols-4 gap-1">
            {albumData.map((data) => (
              <div
                key={data.ImgID}
                className={`relative cursor-pointer aspect-square border-2 rounded-lg ${
                  modalSelected.find((i) => i.ImgID === data.ImgID)
                    ? "border-orange-500"
                    : "border-white bg-white"
                }`}
                onClick={() => toggleAlbumImage(data)}
              >
                <AttachmentPreview
                  fileSeq={data.ImgID}
                  originalUrl={data.ImgURL}
                  thumbnailUrl={data.thumbnailUrl}
                  thumbnailStatus={data.thumbnailStatus}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3 mt-2 px-4">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={albumModal.closeModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={confirmAlbum}
          >
            선택
          </button>
        </div>
      </MessageModal>

      {/* 삭제 확인 */}
      <Dialog visible={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">소식 삭제</h3>
          <p className="mb-4 text-gray-600">
            이 소식을 삭제할까요? 되돌릴 수 없습니다.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setDeleteTarget(null)}
              className="flex-1 py-2 text-gray-700 bg-gray-200 rounded-lg"
            >
              취소
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="flex-1 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600"
            >
              삭제
            </button>
          </div>
        </div>
      </Dialog>

      {/* 완료 */}
      <Dialog visible={showDone} onClose={() => setShowDone(false)}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">소식 등록 완료</h3>
          <p className="mb-4 text-gray-600">
            재원 보호자에게 소식이 전달됩니다.
          </p>
          <button
            onClick={() => setShowDone(false)}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default SendFeed;
